import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsInput, InputOptions } from "../../components/inputs/input";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";
import Decimal from "decimal.js-light";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";

export type InputDecimalEditorOptions = EditorOptions;

export const defaultDecimalNumberEditorOptions: InputDecimalEditorOptions = {
    ...defaultEditorOptions
};

/**
 * Class representing a component with an Decimal input
 */
@CustomElement({
    name: "cjs-input-decimal-editor",
    template: require("./inputDecimalEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsInputDecimalEditor extends CjsEditor<string> {

    /** Attributes options of InputNumberEditor */
    @TypedWebComponentOptions(defaultDecimalNumberEditorOptions)
    public readonly options!: WebComponentOptions<InputDecimalEditorOptions>;

    /** Shortcut for options of Input */
    public get inputOptions(): WebComponentOptions<InputOptions<Decimal>> {
        return this.getInput().options;
    }

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInput] })
    private readonly components!: {
        inputDecimal: CjsInput<Decimal>
    };

    constructor(options?: Partial<InputDecimalEditorOptions>, inputOptions?: Partial<InputOptions<Decimal>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.inputOptions.setWithoutRefresh(inputOptions);
    }

    /** Get input of Editor, where user set data */
    public getInput(): CjsInput<Decimal> {
        return this.components.inputDecimal;
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        // Callback for input components
        this.getInput().changeEvent.on((event: Event) => this._onInputValueChange(event));
        this.getInput().addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._onInputValueChange(e);
            }
        });

    }

    /** Refresh component display */
    public override refresh(): void {

        // Force input type number
        this.inputOptions.setWithoutRefresh({ inputType: "number", enablePrecisionLibrary: true });

        // Refresh
        super.refresh();

        // Try setting decimal value
        this._refreshInputValue();

        // Refresh
        this.getInput().refresh();
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        // Try setting decimal value
        try {
            const value = this._value != null ? new Decimal(this._value) : undefined;
            // Set options
            this.inputOptions.setWithoutRefresh({ value });
        } catch (e) {
            getLogConsole().error(e);
        }
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): string | undefined {
        const value = this.getInput().value;
        if (value == null) {
            return undefined;
        }
        if (!this.inputOptions.fixedPrecisionOnRegister) {
            return value.toString();
        }
        return value.toFixed(this.inputOptions.fixedPrecisionOnRegister);
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().input;
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.inputOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }
}